import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../config';
import FeedbackForm from '../components/FeedbackForm';
import WaitList from '../components/WaitList';
import './FoodPurchaseDecisionMakerPage.scss';

function FoodPurchaseDecisionMakerPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    heightFeet: '',
    heightInches: '',
    height: '', // New field
    weight: '', // New field
    activityLevel: '', // New field
    weightGoal: '',
    medicalCondition: '',
    allergies: '',
    diet: '',
    proteinIntake: false,
    preservatives: false,
    organicPreference: false,
    lowSugarSodium: false,
    avoidRefinedSugarSaturatedFat: false,
    highFiber: false, // New field
    lowCholesterol: false, // New field
    increasedCalcium: false, // New field
  });
  const nextStep = (e) => {
    e.preventDefault();
    setCurrentStep(currentStep + 1);
  };
  const prevStep = (e) => {
    e.preventDefault();
    setCurrentStep(currentStep - 1);
  };

  const [isDataSaved, setIsDataSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const cameraContainerRef = useRef(null);
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');

  useEffect(() => {
    const savedData = localStorage.getItem('foodPurchaseData');
    if (savedData) {
      setFormData(prevData => ({
        ...prevData,
        ...JSON.parse(savedData)
      }));
      setIsDataSaved(true);
    }
  }, []);

  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [showCamera]);

  useEffect(() => {
    if (formData.height) {
      const totalInches = Math.round(parseFloat(formData.height) / 2.54);
      setHeightFeet(Math.floor(totalInches / 12).toString());
      setHeightInches((totalInches % 12).toString());
    }
  }, [formData.height]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    localStorage.setItem('foodPurchaseData', JSON.stringify(formData));
    setIsDataSaved(true);
    setIsEditing(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 1024 * 1024) { // 1MB limit
      processImage(file);
    } else {
      alert('Please select an image smaller than 1MB');
    }
  };

  const processImage = async (file) => {
    setIsLoading(true);
    setError(null);
    setApiResponse(null);

    const formD = new FormData();
    formD.append('file', file);
    
    // Append form data to the request
    Object.keys(formData).forEach(key => {
      formD.append(key, formData[key]);
    });

    try {
      const response = await axios.post(`${config.apiUrl}/make-purchase-decision`, formD, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setApiResponse(response.data);
    } catch (err) {
      console.error('Error processing image:', err);
      let message = 'An error occurred while processing the image. Please try again.';
      if (err.response?.data?.error) {
        message = err.response?.data?.error;
      }
      setError(message);
      fileInputRef.current.value = '';
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEditProfile = () => {
    setApiResponse(null);
    setCurrentStep(1);
    setIsEditing(true);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setShowCamera(true);
      
      // Scroll to camera container after a short delay to ensure it's rendered
      setTimeout(() => {
        if (cameraContainerRef.current) {
          cameraContainerRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
        }
      }, 100);
    } catch (err) {
      console.error("Error accessing the camera: ", err);
      alert("Unable to access the camera. Please make sure you've granted the necessary permissions.");
      setShowCamera(false);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
      canvasRef.current.toBlob((blob) => {
        processImage(blob);
      }, 'image/jpeg');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // foodfit.ai or foodfitai.com

  const faqs = [
    {
      question: "What is FoodFit AI?",
      answer: "FoodFit AI is an intelligent food decision-making assistant that helps you make informed choices about various foods based on your personal health profile and dietary preferences."
    },
    {
      question: "How does FoodFit AI work?",
      answer: "FoodFit AI analyzes the nutritional information of foods using image recognition technology for packaged items, or nutritional databases for fresh produce and common dishes. It then compares this information to your personal health profile to provide personalized recommendations."
    },
    {
      question: "Is my personal health information secure?",
      answer: "Yes, your personal health information is stored locally on your device and is not transmitted to any external servers. We take your privacy seriously."
    },
    {
      question: "What types of foods can I use FoodFit AI for?",
      answer: "FoodFit AI can provide insights on a wide range of foods, including packaged items, fresh produce, and common dishes. For packaged foods, it works best with clear nutrition labels. For other foods, it relies on comprehensive nutritional databases."
    },
    {
      question: "How accurate is FoodFit AI?",
      answer: "While FoodFit AI strives for high accuracy using up-to-date nutritional databases and AI technology, it's important to note that it's a guide and not a substitute for professional medical or nutritional advice."
    },
    {
      question: "Can I update my health profile?",
      answer: "Yes, you can update your health profile at any time by clicking the 'Edit Profile' button on the main page. Keeping your profile current ensures more accurate recommendations."
    },
    {
      question: "What if FoodFit AI doesn't recognize a food item?",
      answer: "For packaged items, ensure the image is clear and well-lit. For other foods, you can manually search our database or input nutritional information if known. We continuously update our food database to improve recognition."
    },
    {
      question: "Can I use FoodFit AI for meal planning?",
      answer: "Yes! FoodFit AI can help you make informed decisions about individual foods, which can be very useful for planning balanced meals that align with your health goals."
    },
    {
      question: "Is FoodFit AI available offline?",
      answer: "Basic functionality and your personal profile are available offline. However, an internet connection is required for image processing, accessing the full food database, and receiving the most up-to-date recommendations."
    },
    {
      question: "How does FoodFit AI handle dietary restrictions or preferences?",
      answer: "FoodFit AI takes into account the dietary preferences and restrictions you specify in your profile, such as vegetarian, vegan, gluten-free, or specific allergies, when providing recommendations."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleHeightChange = (e) => {
    const { name, value } = e.target;
    if (name === 'heightFeet') {
      setHeightFeet(value);
    } else if (name === 'heightInches') {
      setHeightInches(value);
    }
    
    const feet = parseInt(name === 'heightFeet' ? value : heightFeet) || 0;
    const inches = parseInt(name === 'heightInches' ? value : heightInches) || 0;
    const totalInches = feet * 12 + inches;
    const cm = Math.round(totalInches * 2.54);
    
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
      height: cm ? cm.toString() : ''
    }));
  };

  return (
    <div className="food-purchase-decision-maker">
      <div className="logo">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 50" width="200" height="50">
          <text x="10" y="35" fontFamily="Arial, sans-serif" fontSize="24" fontWeight="bold" fill="#4CAF50">
            Food<tspan fill="#007bff">Fit</tspan>Ai
          </text>
        </svg>
      </div>
      <div className="header">
        <h1 className="title">AI-Powered Food Shopping Assistant</h1>
        <p className="subtitle">
          <span className="highlight">Personalized Nutrition</span> at Your Fingertips: 
          Let AI Guide Your Food Purchases Based on Your Unique Health Profile
        </p>
      </div>
      <div className="how-to-use">
        <h2>How to Use FoodFit AI</h2>
        <div className="steps-container">
          <div className="step">
            <div className="step-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
            <h3>1. Set Up Your Profile</h3>
            <p>Enter your health details, dietary preferences, and goals to personalize your experience.</p>
          </div>
          <div className="step">
            <div className="step-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M3 4V1h2v3h3v2H5v3H3V6H0V4h3zm3 6V7h3V4h7l1.83 2H21c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V10h3zm7 9c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-3.2-5c0 1.77 1.43 3.2 3.2 3.2s3.2-1.43 3.2-3.2-1.43-3.2-3.2-3.2-3.2 1.43-3.2 3.2z"/>
              </svg>
            </div>
            <h3>2. Upload or Scan Picture</h3>
            <p>Take a photo or upload an image of the food product to get personalized recommendations.</p>
          </div>
        </div>
      </div>
      {isDataSaved && !isEditing ? (
        <div className="saved-data">
          <h2>Your Saved Profile</h2>
          <div className="profile-header">
            <p>Age: {formData.age}, Gender: {formData.gender}</p>
            <button className="edit-icon" onClick={handleBackToEditProfile} aria-label="Edit profile">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
          </div>
          <div className="image-upload-options">
            <div className="upload-hint">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              <span>Upload or capture an image of a food product to get personalized recommendations. In case of packaged food, capture ingredients for better results.</span>
            </div>
            <div className="file-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <button onClick={() => fileInputRef.current.click()} className="upload-button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/>
                </svg>
                <span>Upload Image</span>
              </button>
            </div>
            <div className="camera-upload">
              <button onClick={() => setShowCamera(true)} className="camera-button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path d="M9 3L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                  <circle cx="12" cy="13" r="3"/>
                </svg>
                <span>Capture Image</span>
              </button>
            </div>
          </div>
          {showCamera && (
            <div className="camera-container" ref={cameraContainerRef}>
              <video ref={videoRef} autoPlay playsInline />
              <div className="camera-controls">
                <button onClick={captureImage}>Capture</button>
                <button onClick={() => setShowCamera(false)}>Cancel</button>
              </div>
              <canvas ref={canvasRef} style={{ display: 'none' }} width="640" height="480" />
            </div>
          )}
        </div>
      ) : (
        <div className="health-form-container container mt-5">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title text-center mb-4">Your Health Profile</h2>
              <div className="progress mb-4">
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${(currentStep / 4) * 100}%` }}
                aria-valuenow={currentStep}
                aria-valuemin="0"
                aria-valuemax="4"
              >
                Step {currentStep} of 4
              </div>
            </div>
              <form onSubmit={handleSubmit}>
                {currentStep === 1 && (
                  <div>
                    <h4 className="mb-3">Step 1: Basic Information</h4>
                    <div className="mb-3">
                      <label htmlFor="age" className="form-label">Your Age:</label>
                      <input
                        type="number"
                        className="form-control"
                        id="age"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="gender" className="form-label">Your Gender:</label>
                      <select
                        className="form-select"
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Your Height:</label>
                      <div className="row">
                        <div className="col">
                          <select
                            className="form-select"
                            id="heightFeet"
                            name="heightFeet"
                            value={formData.heightFeet}
                            onChange={handleHeightChange}
                          >
                            {[...Array(9).keys()].map(i => (
                              <option key={i} value={i}>{i} ft</option>
                            ))}
                          </select>
                        </div>
                        <div className="col">
                          <select
                            className="form-select"
                            id="heightInches"
                            name="heightInches"
                            value={formData.heightInches}
                            onChange={handleHeightChange}
                          >
                            {[...Array(12).keys()].map(i => (
                              <option key={i} value={i}>{i} inch</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="weight" className="form-label">Your Weight (kg):</label>
                      <input
                        type="number"
                        className="form-control"
                        id="weight"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div>
                    <h4 className="mb-3">Step 2: Health Goals</h4>
                    <div className="mb-3">
                      <label htmlFor="activityLevel" className="form-label">Your Activity Level:</label>
                      <select
                        className="form-select"
                        id="activityLevel"
                        name="activityLevel"
                        value={formData.activityLevel}
                        onChange={handleChange}
                      >
                        <option value="">Select activity level</option>
                        <option value="sedentary">Sedentary</option>
                        <option value="moderately active">Moderately Active</option>
                        <option value="very active">Very Active</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="weightGoal" className="form-label">Your Weight Goal:</label>
                      <select
                        className="form-select"
                        id="weightGoal"
                        name="weightGoal"
                        value={formData.weightGoal}
                        onChange={handleChange}
                      >
                        <option value="">Select weight goal</option>
                        <option value="lose">Lose Weight</option>
                        <option value="maintain">Maintain Weight</option>
                        <option value="gain">Gain Weight</option>
                      </select>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div>
                    <h4 className="mb-3">Step 3: Medical Information</h4>
                    <div className="mb-3">
                      <label htmlFor="medicalCondition" className="form-label">Your Medical Condition:</label>
                      <input
                        type="text"
                        className="form-control"
                        id="medicalCondition"
                        name="medicalCondition"
                        value={formData.medicalCondition}
                        onChange={handleChange}
                        placeholder="E.g., Depression, Diabetes, Acne"
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="allergies" className="form-label">Your Allergies:</label>
                      <input
                        type="text"
                        className="form-control"
                        id="allergies"
                        name="allergies"
                        value={formData.allergies}
                        onChange={handleChange}
                        placeholder="E.g., Peanuts, Eggs, Wheat"
                      />
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div>
                    <h4 className="mb-3">Step 4: Dietary Preferences</h4>
                    <div className="mb-3">
                      <label htmlFor="diet" className="form-label">Your Diet Type:</label>
                      <input
                        type="text"
                        className="form-control"
                        id="diet"
                        name="diet"
                        value={formData.diet}
                        onChange={handleChange}
                        placeholder="E.g., Vegetarian, Vegan, Keto, Gluten-Free"
                      />
                    </div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="proteinIntake"
                          name="proteinIntake"
                          checked={formData.proteinIntake}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="proteinIntake">
                          Ok with increasing protein intake
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="preservatives"
                          name="preservatives"
                          checked={formData.preservatives}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="preservatives">
                        Not ok with preservatives
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="organicPreference"
                          name="organicPreference"
                          checked={formData.organicPreference}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="organicPreference">
                        Prefer organic products
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lowSugarSodium"
                          name="lowSugarSodium"
                          checked={formData.lowSugarSodium}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="lowSugarSodium">
                        Prefer low amounts of added sugars and sodium
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="avoidRefinedSugarSaturatedFat"
                          name="avoidRefinedSugarSaturatedFat"
                          checked={formData.avoidRefinedSugarSaturatedFat}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="avoidRefinedSugarSaturatedFat">
                        Avoid refined sugar or saturated fat
                        </label>
                      </div>
                    </div>
                    {/* Add more dietary preference checkboxes here */}
                  </div>
                )}

                <div className="d-flex justify-content-between mt-4 gap-4">
                  {currentStep > 1 && (
                    <button type="button" className="btn btn-secondary" onClick={prevStep}>
                      Previous
                    </button>
                  )}
                  {currentStep < 4 ? (
                    <button type="button" className="btn btn-primary" onClick={nextStep}>
                      Next
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-success">
                      Submit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          
        </div>
      )}
      {isLoading && <div className="loading">Processing image...</div>}
      {error && <div className="error">{error}</div>}
      {apiResponse && (
        <div className={`api-response ${apiResponse.purchase_decision}`}>
          <h2 className="decision">
            Purchase Decision: {apiResponse.purchase_decision.toUpperCase()}
          </h2>
          <div className="score-container">
            <div className="score-bar" style={{width: `${apiResponse.purchase_decision_score * 10}%`}}></div>
            <span className="score-text">{apiResponse.purchase_decision_score}/10</span>
          </div>
          <p className="reason">{apiResponse.reason}</p>
        </div>
      )}
      <WaitList 
        title="Be a FoodFit AI Pioneer"
        subtitle="Get free early access when we release fully featured product and help shape our product!"
        buttonText="Join FoodFit AI Beta"
        successMessage="You're in! We'll notify you soon about your exclusive early access to FoodFit AI."
        placeholderText="Your email for early access"
      />
      <section className="faqs">
        <h2>Frequently Asked Questions</h2>
        {faqs.map((faq, index) => (
          <div key={index} className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(index)}>
              {faq.question}
              <span className="faq-icon">{openFaqIndex === index ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              <p>{faq.answer}</p>
            </div>
          </div>
        ))}
      </section>
      <footer className="feedback-footer">
        <div className="feedback-content">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feedback-icon">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            <path d="M7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z"/>
          </svg>
          <p>
            Have feedback? Reach out to the creator
            <a href="https://x.com/onkarjanwa" target="_blank" rel="noopener noreferrer">
              @onkarjanwa on X
            </a>
          </p>
        </div>
      </footer>
      <FeedbackForm />
    </div>
  );
}

export default FoodPurchaseDecisionMakerPage;